"use client";

import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

interface LazyOnVisibleProps {
  children: ReactNode;
  className?: string;
  minHeight?: number;
  rootMargin?: string;
}

export default function LazyOnVisible({
  children,
  className,
  minHeight = 320,
  rootMargin = "600px 0px",
}: LazyOnVisibleProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) return;

    const element = ref.current;
    if (!element) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <div
      ref={ref}
      className={className}
      style={!isVisible ? { minHeight } : undefined}
    >
      {isVisible ? children : null}
    </div>
  );
}
